import React from "react";
import PropTypes from "prop-types";
import {useTheme} from "connectui/theme/ThemeProvider";
import FlexContainer from "connectui/component/layout/FlexContainer";

const ButtonSegmentedGroup = ({
  children,
  selectedIndex = 0,
  onChange,
  sx = {},
  ...props
}) => {
  const {theme} = useTheme();
  const childCount = React.Children.count(children);

  // Ensure selectedIndex is within valid bounds
  const clampedSelectedIndex = Math.max(
    0,
    Math.min(selectedIndex, childCount - 1),
  );
  const [selected, setSelected] = React.useState(clampedSelectedIndex);

  React.useEffect(() => {
    // Clamp the selectedIndex to valid bounds when it changes
    const validSelectedIndex = Math.max(
      0,
      Math.min(selectedIndex, childCount - 1),
    );
    setSelected(validSelectedIndex);
  }, [selectedIndex, childCount]);

  const childrenWithProps = React.Children.map(children, (child, index) => {
    // Additional safety check to ensure child exists
    if (!child) {
      return null;
    }

    const isSelected = selected === index;
    const isFirst = index === 0;
    const isLast = index === childCount - 1;

    let borderRadius = "0px";
    if (isFirst && isLast) {
      borderRadius = `${theme.radius.lg}px`;
    } else if (isFirst) {
      borderRadius = `${theme.radius.lg}px 0 0 ${theme.radius.lg}px`;
    } else if (isLast) {
      borderRadius = `0 ${theme.radius.lg}px ${theme.radius.lg}px 0`;
    }

    return React.cloneElement(child, {
      ...child.props,
      onClick: (e) => {
        setSelected(index);
        if (onChange) {
          onChange(index);
        }
        if (child.props.onClick) {
          child.props.onClick(e);
        }
      },
      sx: {
        backgroundColor: isSelected ? theme.colors.gray100 : "transparent",
        borderRadius: borderRadius,
        border: "none",
        borderRight: !isLast ? `1px solid ${theme.colors.border}` : "none",
        minWidth: 0,
        height: "40px",
        color: theme.colors.textSecondary,
        "&:hover": {
          backgroundColor: isSelected
            ? theme.colors.gray200
            : theme.colors.gray100,
        },
        ...child.props.sx,
      },
      color: "textSecondary",
    });
  });

  return (
    <FlexContainer
      flexDirection="row"
      alignItems="center"
      sx={{
        display: "inline-flex",
        backgroundColor: theme.colors.white,
        borderRadius: `${theme.radius.lg}px`,
        border: `1px solid ${theme.colors.border}`,
        padding: "0px",
        overflow: "hidden",
        ...sx,
      }}
      {...props}
    >
      {childrenWithProps}
    </FlexContainer>
  );
};

ButtonSegmentedGroup.propTypes = {
  children: PropTypes.node.isRequired,
  selectedIndex: PropTypes.number,
  onChange: PropTypes.func,
  sx: PropTypes.object,
};

export default ButtonSegmentedGroup;
